import { Component, EventEmitter, Injector, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { DropDownListComponent } from '@progress/kendo-angular-dropdowns';
import * as moment from 'moment';
import { take } from 'rxjs/operators';
import { MainFormBase } from '../../../../../components/common/main-form-base/main-form-base';
import { PolluxFormConfig } from '../../../../../components/common/pollux-form-config/pollux-form-config';
import { WorkspaceBase } from '../../../../../components/common/workspace-base/workspace-base';
import { ValuationEventsWorkspaceComponent } from '../../workspaces/valuation-events-workspace/valuation-events-workspace.component';
import { valuationEventsFormConfig } from './valuation-events-form-config';
import { TranslateService } from '@ngx-translate/core';
import { AppLoadingIndicatorService } from '../../../../../services/app-loading-indicator.service';
import { AppStorageService } from '../../../../../services/app-storage.service';

/**
 * Valuation Events form
 */
@Component({
  selector: 'valuation-events-form',
  templateUrl: './valuation-events-form.component.html',
  styleUrls: ['./valuation-events-form.component.css']
})
export class ValuationEventsFormComponent extends MainFormBase<any> implements OnInit, OnDestroy {
  /**
   * The project retriever
   */
  @ViewChild("projectRetriever") public projectRetriever: DropDownListComponent;

  /**
   * Gets the required retrievers
   */
  protected get requiredRetrievers(): string[] {
    return ['Project'];
  }

  /**
   * Sets the default retriever data
   * @param data
   */
  protected setDefaultRetrieverData(data: any) {
    //Add the 0 entry
    if (!this.retrieverData['Project'].find((en) => en.id === 0)) {
      this.retrieverData['Project'].splice(0, 0, {
        id: 0,
        projectName: ''
      });
    }

    //Add the pre-selected entry
    if (data && data.Oid > 0) {
      this.setDefaultRetrieverDataEditMode(data);
    } else if (data && data.Oid == -1) {
      this.setDefaultRetrieverDataNewMode();
    }
  }

  /**
   * The currency
   */
  public currency: string = null;

  /**
   * The unit of length
   */
  public unitOfLength: string = null;

  /**
   * The parent
   */
  @Input() parent: any = null;

  /**
   * Child mode active?
   */
  @Input() childMode = false;

  /**
   * Show the move to the previous item button
   */
  @Input() showPrevBtn = false;

  /**
   * Show the move to the next item button
   */
  @Input() showNextBtn = false;

  /**
   * Emits an event if the move to previous item button was clicked
   */
  @Output() showPrevious = new EventEmitter<void>();

  /**
   * Emits an event if the move to next item button was clicked
   */
  @Output() showNext = new EventEmitter<void>();

  /**
   * Sets the default retriever data in New mode
   */
  private setDefaultRetrieverDataNewMode(): void {
    if (this.parent && this.childMode) {
      if (!this.retrieverData['Project'].find((en) => en.id === this.parent.ProjectID)) {
        this.retrieverData['Project'].splice(1, 0, {
          id: this.parent.ProjectID,
          projectName: this.parent.ProjectName + ', ' + this.parent.Oid
        });
      }
    } else if (this.parent) {
      if (!this.retrieverData['Project'].find((en) => en.id === this.parent.Oid)) {
        this.retrieverData['Project'].splice(1, 0, {
          id: this.parent.Oid,
          projectName: this.parent.Name + ', ' + this.parent.Oid
        });
      }
    }
  }

  /**
   * Sets the default retriever data in edit mode
   * @param data
   */
  private setDefaultRetrieverDataEditMode(data: any): void {
    if (!this.retrieverData['Project'].find((en) => en.id === data.ProjectID)) {
      this.retrieverData['Project'].splice(1, 0, {
        id: data.ProjectID,
        projectName: data.ProjectIDDescription
      });
    }
  }

  /**
   * Sets the OID
   */
  @Input() set oid(value: number) {
    this.currentOid = value;
  }

  /**
   * The workspace
   */
  @Input() workspace: ValuationEventsWorkspaceComponent;

  /**
   * All available templates
   */
  public availableTemplates: any[] = [];

  /**
   * The selectable templates
   */
  public filteredAvailableTemplates: any[] = [];

  /**
   * Property list reference data
   */
  public propertiesListReferenceData: any = null;

  /**
   * The available languages
   */
  public availableLanguageDefinitions: any[] = [];

  /**
   * TranslateService
   */
  private translate: TranslateService;

  /**
   * Constructor
   * @param injector
   * @param appLoadingIndicator
   * @param appStorage
   */
  constructor(protected injector: Injector, private appLoadingIndicator: AppLoadingIndicatorService, private appStorage: AppStorageService) {
    super(injector);
    this.translate = this.injector.get(TranslateService);
  }

  /**
   * Gets the workspace reference
   */
  public get workspaceReference(): WorkspaceBase {
    return this.workspace;
  }

  /**
   * Gets the form configuration
   */
  protected get formConfig(): PolluxFormConfig[] {
    return valuationEventsFormConfig;
  }

  /**
  * Gets the API POST endpoint for saving a single BO
  */
  protected getSaveSingleEndpoint(): string {
    return this.appServiceHelper.getServiceEndpoint('pollux.projectValuation.api', `ValuationPortfolios`);
  }

  /**
  * Gets the API GET endpoint for loading a single BO by it's OID
  * @param oid The BO's OID
  */
  protected getLoadSingleEndpoint(oid: number): string {
    return this.appServiceHelper.getServiceEndpoint('pollux.projectValuation.api', `ValuationPortfolios?oid=${oid}`);
  }

  /**
  * Gets the API GET endpoint for loading report templates
  */
  protected getLoadTemplatesEndpoint(): string {
    return this.appServiceHelper.getServiceEndpoint('pollux.reportDesigner.api', `templates/noJson`);
  }

  /**
  * Gets the API GET endpoint for loading language definitions
  */
  private getLoadLanguageDefinitionEndpoint(): string {
    return this.appServiceHelper.getServiceEndpoint('pollux.dataverse.api', 'dictionary/list')
  }

  /**
   * The required ref lists
   */
  protected get requiredReflists(): string[] {
    return [
      'ref_recordstatus',
      'ref_valuation_businesslinenr',
      'ref_l2_liability_type',
      'ref_template_language',
      'ref_valuation_project_periodofvaluation',
      'ref_l2_deviations_from_instruction',
      'ref_portfolio_valuation_status',
      'ref_l3_leading_method',
      'config_user',
      'config_business_units',
      'ref_l3_display_values_over_time'
    ];
  }

  /**
   * Closes the form
   */
  public close(): void {
    this.eventBus.broadcastEvent('closePortfolioForm');
  }

  /**
   * Performs post-processing of the data
   * Important: Please do not parse any Dates from string in this method!
   * @param data Loaded data
   */
  protected postProcessData(data: any): Promise<any> {
    return new Promise<any>((resolve, reject) => {
      try {
        const availableTemplates = this.additionalLoadPromisesResult.availableTemplates;
        const selectedSoftDeletedTemplate = availableTemplates.find((item) => { return (item.Oid == data.TemplateId) && (item.InvisibleFlag == 1); });
        if (selectedSoftDeletedTemplate) {
          this.availableTemplates = availableTemplates.filter((item) => { return ((item.Oid != data.TemplateId) && (item.InvisibleFlag != 1)) || (item.Oid == data.TemplateId); });
        }
        else {
          this.availableTemplates = availableTemplates.filter((item) => { return item.InvisibleFlag != 1; });
        }

        this.filteredAvailableTemplates = availableTemplates;

        this.availableLanguageDefinitions = this.additionalLoadPromisesResult.availableLanguageDefinitions

        this.propertiesListReferenceData = {
          Oid: data.Oid,
          ProjectID: data.ProjectID
        };
        this.currency = data.CurrencySymbol;
        this.unitOfLength = data.UnitOfLengthDescription;

        if (this.childMode) {
          this.postProcessChildData(data, this.parent);
        }
        else {
          this.postProcessRegularData(data, this.parent);
        }

        resolve(data);
      } catch (e) {
        reject(e);
      }
    });
  }

  /**
   * Post-processes child data
   * @param data
   * @param parent
   */
  postProcessChildData(data: any, parent: any) {
    if (data.Oid < 0) {
      data.LanguageId = 1;
      data.PeriodOfValuationID = 2;
      if (parent) { //Parent is a VE
        data.ProjectID = parent.ProjectID;
        data.ProjectIDDescription = parent.ProjectIDDescription;

        data.Client = parent.Client;

        data.CurrencySymbol = parent.currencySymbol;
        this.currency = data.CurrencySymbol;
        this.unitOfLength = data.UnitOfLengthDescription;

        const projectNameWithId = String(data.ProjectIDDescription);
        if (projectNameWithId.lastIndexOf(',') >= 0) {
          data.ProjectName = projectNameWithId.substring(0, projectNameWithId.lastIndexOf(',')).trim();
        }
        else {
          data.ProjectName = projectNameWithId;
        }

        data.ParentValuationEventName = parent.ValuationPortfolioName;
        data.ParentId = parent.Oid;
        data.ValuationDate = parent.ValuationDate;

        data.ClientManual = parent.ClientManual;

        data.LiabilityTypeId = parent.LiabilityTypeId;
      }
    }
  }

  /**
   * Post-processes regular (non-child) data
   * @param data
   * @param parent
   */
  private postProcessRegularData(data: any, parent: any): void {
    if (data.Oid < 0) {
      data.LanguageId = 1;
      data.PeriodOfValuationID = 2;
      if (parent) { //Parent is a project
        this.postProcessRegularDataWithParent(data, parent);
      }
    }
  }

  /**
   * ost-processes regular (non-child) data with parent
   * @param data
   * @param parent
   */
  private postProcessRegularDataWithParent(data: any, parent: any) {
    data.ProjectID = parent.Oid;
    data.ProjectIDDescription = parent.Name + ', ' + parent.Oid;

    data.Client = parent.ClientId;
    data.ClientIdDescription = parent.ClientIdDescription;

    data.CurrencySymbol = parent.currencySymbol;
    this.currency = data.CurrencySymbol;
    this.unitOfLength = data.UnitOfLengthDescription;

    data.DisplayValuesOverTimeId = parent.DisplayValuesOverTimeId;

    const clientNameWithId = String(data.ClientIdDescription);
    if (clientNameWithId.lastIndexOf(',') >= 0) {
      data.ClientIdDescription = clientNameWithId.substring(0, clientNameWithId.lastIndexOf(',')).trim();
    }

    const projectNameWithId = String(data.ProjectIDDescription);
    if (projectNameWithId.lastIndexOf(',') >= 0) {
      data.ProjectName = projectNameWithId.substring(0, projectNameWithId.lastIndexOf(',')).trim();
    }
    else {
      data.ProjectName = projectNameWithId;
    }

    data.ClientManual = parent.ClientManual;

    data.LiabilityTypeId = parent.LiabilityTypeId;

    let name = '';
    if (data.ClientManual) {
      name += '_' + data.ClientManual;
    }

    if (data.ProjectName) {
      name += '_' + data.ProjectName;
    }

    data.ValuationPortfolioName = name;
  }

  /**
   * If a validation error occurs, this ethd is called.
   * Use it to inform the user, enable buttons, etc
   * @param _data The data
   */
  public handleValidationError(_data: any) {
    this.eventBus.broadcastEvent("openConfirmDialog", {
      hideConfirmButton: true,
      cancel: () => {
        //Nothing
      }
    }, 'fillRequiredFieldsDialog');
  }

  /**
   * Evaluates the save result. Returns true if saving was successful
   * @param result The save result
   */
  public evaluateSaveResult(result: any): boolean {
    return result?.m_Item1;
  }

  /**
   * Perform any pre-save activities here, for example user interaction, user queries etc.
   * If the Promise is resolved with true, saving will continue. If iit's resolved with false or rejected, saving is cancelled.
   * @param _data The data that should be saved
   */
  public handlePreSave(data: any): Promise<boolean> {
    return new Promise((resolve) => {
      const leadingMethodInvalid = this.formData.controls['LeadingMethodId'].invalid
      if (leadingMethodInvalid) {
        this.eventBus.broadcastEvent("openConfirmDialog", {
          success: () => {
            resolve(false);
          }
        }, 'leadingMethodNotFilled');
      }
      else {
        resolve(true);
      }
    });
  }

  /**
   * If the save process was aborted, this methd is called.
   * You can use it to re-enable buttons, inform the user, etc
   * @param _data The data that should be saved originally
   */
  public handleSaveAborted(_data: any): void {
    this.eventBus.broadcastEvent('enableSaveButtonIfRequired');
  }

  /**
   * If the save call was triggered, this method is called.
   * Use it to inform the user, close the form or trigger grid reloading.
   * @param saveResult Th result returned from the API
   */
  protected handlePostSave(saveResult: any): void {
    this.eventBus.broadcastEvent('valuationPortfoliosDataSaved', saveResult, this.currentOid === -1);
    this.close();
  }

  /**
   * If an error occurred during saving, this method is called.
   * Use it to re-enable buttons, inform the user, etc.
   * Note: This occurs on errors and doesn't evaluate the saveResult returned from the API, since this can be different between calls.
   * @param error The error information
   */
  protected handleSaveError(error: any): void {
    alert('An error occurred while saving your data!');
    console.error(error);
    this.eventBus.broadcastEvent('enableSaveButtonIfRequired');
  }

  /**
   * The next button was clicked
   */
  public next(): void {
    this.showNext.emit();
  }

  /**
   * The previous button was clicked
   */
  public previous(): void {
    this.showPrevious.emit();
  }

  /**
   * Initializes the component
   */
  ngOnInit(): void {
    this.onEvents.push(this.eventBus.subscribeEvent('savePortfolioData', () => {
      this.save();
    }));

    this.onEvents.push(this.eventBus.subscribeEvent('cancelPortfolioDataChanges', () => {
      this.close();
    }));

    this.onEvents.push(this.eventBus.subscribeEvent('valuationTabSelect', () => {
      this.close();
    }));

    this.onEvents.push(this.eventBus.subscribeEvent('cancelProjectDataChanges', () => {
      //portfolios could also be created from projects.. so react on "cancel" there too
      this.close();
    }));

    const availableTemplatesUrl = this.getLoadTemplatesEndpoint();

    this.getObservableOnLoad('availableTemplates', this.httpClient.get(availableTemplatesUrl).pipe(take(1)));
    this.getObservableOnLoad('availableLanguageDefinitions', this.httpClient.get(this.getLoadLanguageDefinitionEndpoint()).pipe(take(1)))
    this.setArgusPortfolioIdIsEditable();

    this.init().then(() => {
      this.eventBus.broadcastEvent('hideParticipantsIcon', this.isNew);

      this.eventBus.broadcastEvent('showSaveCancelNavigationAction', true);


      //inform infobar if required
      this.eventBus.broadcastEvent('project-valuation-infobar-item-changed', {
        mode: 'valuationProject',
        item: this.formData.getRawValue(),
        itemIdentityProperty: 'Oid'
      });

      this.datasetStatusUpdated(this.formData.controls['RecordStatusId'].value);

      if (this.formData.controls['Oid'].value < 0) {
        //Build the VE name automatically for new VEs
        this.buildValuationPortfolioName();
      }

      setTimeout(() => {
        this.registerRetrieverComponent(this.projectRetriever, 'Project');
      });
    });
  }

  /**
   * Starts an export to Argus
   */
  public exportToArgus(): void {
    if (this.formData.controls['NumberOfPropertyCalc'].value) {
      this.eventBus.broadcastEvent("argusDialog", {
        success: () => {
          this.appLoadingIndicator.show();
          this.eventBus.broadcastEvent("exportToArgusPortfolio", [this.formData]);
        },
        cancel: () => {
          // Nothing
        },
        dialogContent: () => { return this.translate.instant('exportToArgusConfirmDialog.message') },
        shouldShowCloseButton: () => {
          return false;
        }
      }, 'exportToArgusConfirmDialog');
    }
  }

  /**
   *
   * Called when the dataset status was updated
   * @param newRecordStatusId
   */
  public datasetStatusUpdated(newRecordStatusId): void {
    const formReadOnly = newRecordStatusId == 2;

    this.setReadOnly(formReadOnly);
  }

  /**
   * Filters templates by language
   * @param languageDefinitionId
   */
  public filterTemplatesByLanguage(languageDefinitionId: any) {
    this.filteredAvailableTemplates = this.availableTemplates.filter((template) => {
      return template.templateLanguages.indexOf(parseInt(languageDefinitionId)) > -1;
    });
    const selectedTemplateID = this.formData.controls['TemplateId'].value;
    if (selectedTemplateID && !this.filteredAvailableTemplates.find(t => t.id === selectedTemplateID)) {
      this.formData.controls['TemplateId'].setValue(null);
    }
  }

  /**
   * Applies the project name
   */
  public applyProjectName(): void {
    const projectId = this.formData.controls['ProjectID'].value;
    const retrieverEntry = this.retrieverData['Project'].find((en) => en.id === projectId);

    if (retrieverEntry) {
      this.currency = retrieverEntry.currencySymbol;
      this.formData.controls['ProjectIDDescription'].setValue(`${retrieverEntry.id}, ${retrieverEntry.projectName}`);
      this.formData.controls['ProjectName'].setValue(retrieverEntry.projectName);
    }
    else {
      this.currency = '';
      this.formData.controls['ProjectIDDescription'].setValue('0, ');
      this.formData.controls['ProjectName'].setValue('');
    }
    this.buildValuationPortfolioName();
  }

  /**
   * Builds the Valuation Event name
   */
  public buildValuationPortfolioName(): void {
    let name = '';

    const oid = this.formData.controls['Oid'].value;
    const clientManual = this.formData.controls['ClientManual'].value;
    const projectName = this.formData.controls['ProjectName'].value;
    const remark = this.formData.controls['Remark'].value;

    if (oid) {
      const valuationDate = this.formData.controls['ValuationDate'].value;
      if (valuationDate) {
        name = moment(new Date(valuationDate)).format("yyyyMMDD");
      }
    }

    if (clientManual) {
      name += '_' + clientManual;
    }

    if (projectName) {
      name += '_' + projectName;
    }

    if (remark) {
      name += '_' + remark;
    }

    this.formData.controls['ValuationPortfolioName'].setValue(name);
  }

  /**
   * Disables the Argus portfolio ID for non-admins
   */
  private setArgusPortfolioIdIsEditable() {
    this.formConfig.find(formConfigElement => formConfigElement.name == 'ArgusPortfolioId').disabled = !this.appStorage.userIsInRole(['Admin']);
  }
}
